/**
 * Scroll-reveal motion system: the JS half of the markup contract documented
 * in motion.css. It only toggles classes and inline --motion-order values;
 * every animated property lives in CSS, so the main thread stays free.
 *
 * - [data-reveal] and [data-reveal-image]/.motion-reveal-image elements are
 *   revealed the first time they enter the viewport (one-shot).
 * - [data-reveal-group] containers are observed as a whole: when the group
 *   enters, all of its items reveal together and the inline --motion-order
 *   (DOM order, capped) drives the stagger cascade via transition-delay.
 *   With the value "children" every direct child of the group is an item,
 *   so markup rendered by shared snippets or section blocks can join the
 *   cascade without being marked element by element. Only fade/rise items
 *   consume stagger slots; images inherit --motion-order from the item that
 *   contains them, keeping card and zoom in step.
 *
 * The module bails out entirely when html.motion-enabled is missing (no JS
 * flag, theme editor) or when the user prefers reduced motion: in both cases
 * motion.css keeps the content fully visible and static.
 *
 * New nodes added to the DOM (load-more, filtering, Section Rendering
 * patches) are picked up by the MutationObserver below, including items
 * landing inside a group that already fired, which then get their own
 * observer. A morph that strips motion-revealed from a handled element gets
 * the element re-observed, so it reveals again instead of staying hidden.
 */

const ITEM_SELECTOR = '[data-reveal], [data-reveal-image], .motion-reveal-image';
const GROUP_SELECTOR = '[data-reveal-group]';
const REVEALED_CLASS = 'motion-revealed';

/* Caps the cascade so a long group (a big grid) tops out around one second
   of delay instead of queueing seconds of invisible content. */
const MAX_STAGGER_ORDER = 11;

const registered = new WeakSet();

/* Groups observed and not yet fired: their members are revealed by the group
   itself, anything joining after the fire needs its own observer. */
const pendingGroups = new WeakSet();

function reveal(element) {
  element.classList.add(REVEALED_CLASS);
}

/* Constructed conditionally so browsers without IntersectionObserver don't
   throw at module evaluation; init() then drops the motion-enabled flag. */
const observer =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries, io) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            io.unobserve(entry.target);

            if (entry.target.matches(GROUP_SELECTOR)) {
              pendingGroups.delete(entry.target);
              for (const item of groupItems(entry.target)) reveal(item);
            } else {
              reveal(entry.target);
            }
          }
        },
        /* Slight bottom inset so elements start animating once they are
           genuinely on screen, not while still flush with the viewport edge. */
        { rootMargin: '0px 0px -8% 0px' }
      )
    : null;

// A block or shared snippet rendered inside a "children" group can emit a
// non-visual sibling alongside its real content — e.g. contrast-override's
// `{% style %}` tag, or a per-item `<script>` — with no visual box of its
// own, so it can never meaningfully be "revealed". Left uncounted, one of
// these ahead of every item skews everyone's --motion-order (concretely:
// blocks/_product-editorial-card.liquid's contrast-override call doubles the
// intended 0/1/2 stagger to 1/3/5).
const NON_VISUAL_TAGS = new Set(['STYLE', 'SCRIPT', 'TEMPLATE']);

/** @param {Element} el */
function isRevealableChild(el) {
  return !NON_VISUAL_TAGS.has(el.tagName);
}

/**
 * Everything a group reveals together: its marked descendants, plus its
 * direct children in "children" mode. Additive on purpose, so an image
 * marked inside a child still gets its reveal class.
 *
 * @param {Element} group
 * @returns {Element[]}
 */
function groupItems(group) {
  const items = [...group.querySelectorAll(ITEM_SELECTOR)];

  if (group.getAttribute('data-reveal-group') === 'children') {
    for (const child of group.children) {
      if (isRevealableChild(child) && !items.includes(child)) items.push(child);
    }
  }

  return items;
}

/**
 * The items that advance the stagger counter: direct children in "children"
 * mode, [data-reveal] elements otherwise. Images never take a slot of their
 * own; --motion-order is an inherited custom property, so a nested image
 * follows the order of the item that contains it and its zoom runs in step
 * with that item's reveal.
 *
 * @param {Element} group
 * @returns {Iterable<Element>}
 */
function groupPrimaryItems(group) {
  if (group.getAttribute('data-reveal-group') === 'children') {
    return [...group.children].filter(isRevealableChild);
  }
  return group.querySelectorAll('[data-reveal]');
}

/**
 * Registers a group: primary items get their stagger order assigned in DOM
 * order and the container (not the items) is observed.
 *
 * @param {Element} group
 */
function registerGroup(group) {
  let order = 0;

  for (const item of groupPrimaryItems(group)) {
    if (registered.has(item)) continue;
    item.style.setProperty('--motion-order', String(Math.min(order, MAX_STAGGER_ORDER)));
    order += 1;
  }

  for (const item of groupItems(group)) registered.add(item);

  pendingGroups.add(group);
  observer?.observe(group);
}

/**
 * Scans a subtree and wires up every group and standalone item found.
 * Safe to call repeatedly: elements are only registered once.
 *
 * @param {Element | Document} root
 */
function setup(root) {
  const targets = [];
  if (root instanceof Element && root.matches(`${GROUP_SELECTOR}, ${ITEM_SELECTOR}`)) targets.push(root);
  targets.push(...root.querySelectorAll(`${GROUP_SELECTOR}, ${ITEM_SELECTOR}`));

  for (const element of targets) {
    if (registered.has(element)) continue;

    if (element.matches(GROUP_SELECTOR)) {
      registered.add(element);
      registerGroup(element);
    } else {
      registered.add(element);

      /* A pending group reveals its members itself (groupItems is collected
         again when it fires); an item landing inside a group that already
         fired gets its own observer instead of being lost. */
      const group = element.closest(GROUP_SELECTOR);
      if (!group || !pendingGroups.has(group)) observer?.observe(element);
    }
  }
}

function init() {
  if (!document.documentElement.classList.contains('motion-enabled')) return;

  if (!observer) {
    /* No IntersectionObserver support: undo the flag right away so the
       hidden initial state can't strand content. */
    document.documentElement.classList.remove('motion-enabled');
    return;
  }

  /* Boot signal for the failsafe timeout in snippets/scripts.liquid. */
  document.documentElement.dataset.motionReady = 'true';

  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  setup(document);

  /* Content rendered after load (load-more pagination, facet results,
     recommendations) joins the system as soon as it lands in the DOM. The
     attribute branch covers Section Rendering morphs stripping the revealed
     class from elements this module already handled. */
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) setup(node);
        }
        continue;
      }

      const { target } = mutation;
      if (!(target instanceof Element) || !registered.has(target)) continue;
      if (target.classList.contains(REVEALED_CLASS) || !target.matches(ITEM_SELECTOR)) continue;

      const group = target.closest(GROUP_SELECTOR);
      if (!group || !pendingGroups.has(group)) observer?.observe(target);
    }
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
